class RaptorTheme {
  constructor() {
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.initNavigation();
    this.initStickyHeader();
    this.initSearch();
    this.initHeroSliders();
    this.initGosCardPrices();
    this.initProduct();
    this.initRecommendations();
    this.initFacets();
    this.initQuantities();
    this.initGiftTiers();
    this.initCartOffer();
    this.initFooterDetails();
    this.initAddresses();
    this.initSiteModal();
  }

  openOverlay() {
    if (this.overlay) this.overlay.hidden = false;
    document.body.classList.add('drawer-open');
  }

  closeOverlay() {
    if (this.overlay) this.overlay.hidden = true;
    document.body.classList.remove('drawer-open');
  }

  initNavigation() {
    const menu = document.querySelector('[data-mobile-menu]');
    const open = document.querySelector('[data-menu-toggle]');
    const close = document.querySelector('[data-menu-close]');
    if (!menu || !open) return;
    const hide = () => {
      menu.classList.remove('is-open');
      open.setAttribute('aria-expanded', 'false');
      this.closeOverlay();
    };
    open.addEventListener('click', () => {
      menu.classList.add('is-open');
      open.setAttribute('aria-expanded', 'true');
      this.openOverlay();
    });
    close?.addEventListener('click', hide);
    this.overlay?.addEventListener('click', hide);
    menu.querySelectorAll('.site-nav__submenu-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const item = toggle.closest('.site-nav__item');
        const isOpen = item.classList.toggle('submenu-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  initStickyHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header?.classList.contains('site-header--sticky')) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 34);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  initSearch() {
    const panel = document.querySelector('[data-header-search]');
    const toggle = document.querySelector('[data-search-toggle]');
    const close = document.querySelector('[data-search-close]');
    if (!panel || !toggle) return;
    const hide = () => {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
      if (!panel.hidden) panel.querySelector('input')?.focus();
    });
    close?.addEventListener('click', hide);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') hide(); });
  }

  initHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
      const slides = [...slider.querySelectorAll('[data-slide]')];
      const dots = [...slider.querySelectorAll('[data-slide-dot]')];
      const pause = slider.querySelector('[data-slider-pause]');
      if (slides.length < 2) return;
      let index = 0;
      let playing = slider.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      const start = () => {
        window.clearInterval(timer);
        if (playing) timer = window.setInterval(() => show(index + 1), Number(slider.dataset.speed) || 5000);
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      pause?.addEventListener('click', () => {
        playing = !playing;
        pause.textContent = playing ? 'Ⅱ' : '▶';
        pause.setAttribute('aria-label', playing ? 'Mettre le diaporama en pause' : 'Lire le diaporama');
        start();
      });
      start();
    });
  }

  initProduct() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      root.querySelectorAll('[data-media-target]').forEach((button) => {
        button.addEventListener('click', () => {
          root.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('product-media--hidden', media.dataset.mediaId !== button.dataset.mediaTarget));
          root.querySelectorAll('[data-media-target]').forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
        });
      });
      const select = root.querySelector('[data-variant-select]');
      const json = root.querySelector('[data-product-json]');
      const form = root.querySelector('.product-form');
      const addSubmit = root.querySelector('[data-add-to-cart]');
      const submitButtons = [...root.querySelectorAll('[data-product-submit]')];
      const stock = root.querySelector('[data-stock-status]');
      const stockText = root.querySelector('[data-stock-text]');
      const price = root.querySelector('[data-price]');
      if (!select || !json || !form || !addSubmit || !submitButtons.length) return;
      const variants = JSON.parse(json.textContent);
      const bundleMap = window.RaptorGosBundles || {};
      const wheyMap = window.RaptorGosWhey || {};
      const technicalCache = new Map();
      const money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: root.dataset.currency || 'EUR'
      });
      const formatMoney = (cents) => money.format(Number(cents) / 100);
      const bundleForVariant = (variant) => variant?.sku ? bundleMap[String(variant.sku)] : null;
      const technicalVariant = async (handle) => {
        if (technicalCache.has(handle)) return technicalCache.get(handle);
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Composant Shopify introuvable : ${handle}. Importez d’abord le CSV fourni avec ce thème.`);
        const product = await response.json();
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant?.id || variant.available === false) throw new Error(`Composant indisponible : ${product.title || handle}.`);
        const result = {
          id: Number(variant.id),
          title: product.title || handle,
          handle,
          price: Number(variant.price)
        };
        technicalCache.set(handle, result);
        return result;
      };
      const addConfiguredItems = async ({
        items,
        parentTitle,
        parentHandle = '',
        parentOption = '',
        parentSku,
        expectedTotal,
        checkout = false
      }) => {
        const cartResponse = await fetch(root.dataset.cartJsonUrl || `${root.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!cartResponse.ok) throw new Error('Impossible de vérifier le panier avant l’ajout de l’offre.');
        const currentCart = await cartResponse.json();
        const sameOfferAlreadyPresent = currentCart.items?.some((line) => String(line.properties?._gos_bundle_sku || '') === String(parentSku));
        if (sameOfferAlreadyPresent) {
          throw new Error('Cette offre est déjà présente dans votre panier. Modifiez-la depuis le panier ou choisissez une autre offre.');
        }

        const instance = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const expanded = [];
        let slot = 0;
        let configuredTotal = 0;
        for (const item of items) {
          const variant = await technicalVariant(item.handle);
          const quantity = Math.max(1, Number(item.quantity || 1));
          const configuredUnitPrice = Number(item.price);
          if (!Number.isInteger(configuredUnitPrice) || configuredUnitPrice <= 0 || configuredUnitPrice > 4499) {
            throw new Error(`Prix technique invalide pour ${item.label || item.handle}.`);
          }
          if (variant.price !== configuredUnitPrice) {
            throw new Error(`Le prix Shopify de ${item.label || variant.title} ne correspond pas à l’offre affichée. Réimportez le CSV fourni avant de vendre cette offre.`);
          }
          for (let unit = 0; unit < quantity; unit += 1) {
            slot += 1;
            configuredTotal += configuredUnitPrice;
            const displayTitle = item.label || variant.title;
            const offerLabel = [parentTitle, parentOption].filter(Boolean).join(' — ');
            expanded.push({
              id: variant.id,
              quantity: 1,
              properties: {
                _gos_bundle_instance: instance,
                _gos_bundle_parent: parentTitle,
                _gos_bundle_parent_handle: parentHandle,
                _gos_bundle_option: parentOption,
                _gos_bundle_sku: parentSku,
                _gos_bundle_slot: String(slot),
                _gos_bundle_label: displayTitle,
                _gos_unit_price: String(configuredUnitPrice),
                _gos_line_handle: variant.handle,
                // Les propriétés sans préfixe "_" restent visibles dans le
                // checkout Shopify. Elles donnent donc un libellé client propre
                // même avant la mise à jour définitive des titres du catalogue.
                Produit: displayTitle,
                Offre: offerLabel
              }
            });
          }
        }
        if (new Set(expanded.map((line) => line.id)).size !== expanded.length) {
          throw new Error('L’offre utilise encore deux fois la même variante technique. Réimportez le nouveau CSV avant de réessayer.');
        }
        if (Number.isInteger(Number(expectedTotal)) && configuredTotal !== Number(expectedTotal)) {
          throw new Error('Le total technique de l’offre ne correspond pas au prix affiché. L’ajout a été interrompu.');
        }
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: expanded })
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Ajout du Pack impossible.');
        }
        // Toute offre configurée passe par le panier : les lignes, les cadeaux et
        // l’intégration de paiement y sont validés avant le submit natif.
        window.location.assign(root.dataset.cartUrl);
      };
      const setPurchaseAvailability = (available) => {
        submitButtons.forEach((button) => { button.disabled = !available; });
      };
      const applyVariant = (variant, updatePrice = true) => {
        if (!variant) {
          select.value = '';
          setPurchaseAvailability(false);
          addSubmit.textContent = root.dataset.soldOutLabel;
          stock?.classList.add('is-unavailable');
          if (stockText) stockText.textContent = root.dataset.soldOutLabel;
          return;
        }
        const bundle = bundleForVariant(variant);
        const purchasable = Boolean(bundle) || variant.available;
        select.value = String(variant.id);
        setPurchaseAvailability(purchasable);
        addSubmit.textContent = purchasable ? root.dataset.addLabel : root.dataset.soldOutLabel;
        stock?.classList.toggle('is-unavailable', !purchasable);
        if (stockText) stockText.textContent = purchasable ? root.dataset.inStockLabel : root.dataset.soldOutLabel;
        if (price && updatePrice) {
          const displayPrice = Number(bundle?.total || variant.price);
          const compareAt = bundle ? 0 : Number(variant.compare_at_price || 0);
          const current = `<span class="price__current">${formatMoney(displayPrice)}</span>`;
          const compare = compareAt > displayPrice ? `<s class="price__compare">${formatMoney(compareAt)}</s>` : '';
          price.innerHTML = `${compare}${current}`;
          price.classList.toggle('price--sale', Boolean(compare));
        }
        if (variant.featured_media) root.querySelector(`[data-media-target="${variant.featured_media.id}"]`)?.click();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      if (root.hasAttribute('data-whey-offer')) {
        const tiers = [...root.querySelectorAll('[data-whey-tier]')];
        const slots = [...root.querySelectorAll('[data-whey-slot]')];
        const slotRows = [...root.querySelectorAll('[data-whey-slot-row]')];
        const error = root.querySelector('[data-whey-error]');
        const selectedTier = () => tiers.find((tier) => tier.checked) || tiers[0];
        const activeSlots = () => slots.filter((slot) => !slot.disabled);
        const variantForId = (id) => variants.find((variant) => String(variant.id) === String(id));

        const updateOfferPrice = () => {
          const tier = selectedTier();
          if (!tier || !price) return;
          const total = Number(tier.dataset.wheyTotal);
          const compareAt = Number(tier.dataset.wheyCompare);
          const compare = compareAt > total ? `<s class="price__compare">${formatMoney(compareAt)}</s>` : '';
          price.innerHTML = `${compare}<span class="price__current">${formatMoney(total)}</span>`;
          price.classList.toggle('price--sale', Boolean(compare));
        };

        const syncWheySelection = () => {
          const count = Number(selectedTier()?.value || 1);
          slotRows.forEach((row, index) => {
            const active = index < count;
            row.hidden = !active;
            const slot = row.querySelector('[data-whey-slot]');
            if (slot) slot.disabled = !active;
          });

          const chosen = activeSlots().map((slot) => variantForId(slot.value));
          const available = chosen.length === count && chosen.every((variant) => variant?.available);
          const firstVariant = chosen[0];
          if (firstVariant) applyVariant(firstVariant, false);
          setPurchaseAvailability(available);
          addSubmit.textContent = available ? root.dataset.addLabel : root.dataset.soldOutLabel;
          stock?.classList.toggle('is-unavailable', !available);
          if (stockText) stockText.textContent = available ? root.dataset.inStockLabel : root.dataset.soldOutLabel;
          updateOfferPrice();
        };

        tiers.forEach((tier) => tier.addEventListener('change', syncWheySelection));
        slots.forEach((slot) => slot.addEventListener('change', syncWheySelection));

        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (form.dataset.submitting === 'true') return;

          const chosen = activeSlots().map((slot) => variantForId(slot.value)).filter(Boolean);
          const expectedCount = Number(selectedTier()?.value || 1);
          const actualCount = chosen.length;
          if (actualCount !== expectedCount) {
            if (error) {
              error.hidden = false;
              error.textContent = 'Une saveur sélectionnée n’est plus disponible. Veuillez en choisir une autre.';
            }
            syncWheySelection();
            return;
          }

          form.dataset.submitting = 'true';
          form.setAttribute('aria-busy', 'true');
          submitButtons.forEach((button) => { button.disabled = true; });
          if (error) error.hidden = true;

          try {
            const tier = String(expectedCount);
            const isCheckout = event.submitter?.hasAttribute('data-buy-now');
            if (tier === '1') {
              const response = await fetch(root.dataset.cartAddUrl, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ items: [{ id: Number(chosen[0].id), quantity: 1 }] })
              });
              if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.description || payload.message || 'Ajout impossible.');
              }
              window.location.assign(root.dataset.cartUrl);
              return;
            }
            const items = chosen.map((variant, index) => {
              const flavor = variant.option1 || variant.title;
              const mappedHandles = wheyMap[tier]?.[flavor];
              const handle = Array.isArray(mappedHandles) ? mappedHandles[index] : mappedHandles;
              if (!handle) throw new Error(`Configuration Whey manquante pour ${flavor}. Réimportez le CSV final.`);
              return {
                handle,
                quantity: 1,
                price: Number(selectedTier()?.dataset.wheyTotal || 0) / expectedCount,
                label: `Whey Isolate Native 94% — ${flavor}`
              };
            });
            await addConfiguredItems({
              items,
              parentTitle: 'Whey Isolate Native 94%',
              parentHandle: root.dataset.productHandle,
              parentOption: `${tier} pots`,
              parentSku: `WHEY-${tier}-POTS`,
              expectedTotal: Number(selectedTier()?.dataset.wheyTotal || 0),
              checkout: isCheckout
            });
          } catch (requestError) {
            delete form.dataset.submitting;
            form.removeAttribute('aria-busy');
            syncWheySelection();
            if (error) {
              error.hidden = false;
              error.textContent = requestError.message || 'Une erreur est survenue. Veuillez réessayer.';
            }
          }
        });

        window.addEventListener('pageshow', () => {
          delete form.dataset.submitting;
          form.removeAttribute('aria-busy');
          syncWheySelection();
        });
        syncWheySelection();
        return;
      }

      select.addEventListener('change', () => {
        applyVariant(variants.find((item) => String(item.id) === select.value));
      });
      root.querySelectorAll('[data-product-option]').forEach((option) => {
        option.addEventListener('change', () => {
          const selected = [...root.querySelectorAll('[data-product-option]:checked')]
            .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
            .map((input) => input.value);
          applyVariant(variants.find((variant) => variant.options.every((value, index) => value === selected[index])));
        });
      });
      form.addEventListener('submit', async (event) => {
        const selectedVariant = variants.find((variant) => String(variant.id) === select.value);
        const bundle = bundleForVariant(selectedVariant);
        if ((!bundle && !selectedVariant?.available) || form.dataset.submitting === 'true') {
          event.preventDefault();
          return;
        }
        if (bundle) {
          event.preventDefault();
          form.dataset.submitting = 'true';
          form.setAttribute('aria-busy', 'true');
          submitButtons.forEach((button) => { button.disabled = true; });
          const error = root.querySelector('[data-gos-bundle-error]');
          if (error) error.hidden = true;
          try {
            const items = bundle.items.map((item) => ({ ...item, quantity: Number(item.quantity || 1) }));
            await addConfiguredItems({
              items,
              parentTitle: bundle.parentTitle,
              parentHandle: bundle.parentHandle,
              parentOption: bundle.option,
              parentSku: selectedVariant.sku,
              expectedTotal: Number(bundle.total),
              checkout: event.submitter?.hasAttribute('data-buy-now')
            });
          } catch (requestError) {
            delete form.dataset.submitting;
            form.removeAttribute('aria-busy');
            submitButtons.forEach((button) => { button.disabled = false; });
            if (error) {
              error.hidden = false;
              error.textContent = requestError.message || 'Ajout du Pack impossible.';
            }
          }
          return;
        }
        form.dataset.submitting = 'true';
        form.setAttribute('aria-busy', 'true');
        submitButtons.forEach((button) => { button.disabled = true; });
      });
      window.addEventListener('pageshow', () => {
        delete form.dataset.submitting;
        form.removeAttribute('aria-busy');
        applyVariant(variants.find((variant) => String(variant.id) === select.value));
      });
      applyVariant(variants.find((variant) => String(variant.id) === select.value));
    });
  }

  initGosCardPrices() {
    const products = window.RaptorGosBundleProducts || {};
    const language = document.documentElement.lang || 'fr-FR';
    document.querySelectorAll('[data-gos-card-handle]').forEach((card) => {
      const config = products[card.dataset.gosCardHandle];
      const price = card.querySelector('[data-price]');
      if (!config || !price) return;
      const currency = document.querySelector('[data-product-root]')?.dataset.currency || 'EUR';
      const money = new Intl.NumberFormat(language, { style: 'currency', currency });
      const from = Number(config.from || 0);
      const to = Number(config.to || from);
      price.innerHTML = `${to > from ? '<span class="price__from">À partir de</span>' : ''}<span class="price__current">${money.format(from / 100)}</span>`;
      price.classList.remove('price--sale');
    });
  }

  initQuantities() {
    document.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      const announceChange = () => input.dispatchEvent(new Event('input', { bubbles: true }));
      control.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const minimum = Number.isFinite(Number(input.min)) ? Number(input.min) : 0;
        input.value = Math.max(minimum, Number(input.value) - 1);
        announceChange();
      });
      control.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const maximum = Number.isFinite(Number(input.max)) && input.max !== '' ? Number(input.max) : Number.POSITIVE_INFINITY;
        input.value = Math.min(maximum, Number(input.value) + 1);
        announceChange();
      });
    });
  }

  initGiftTiers() {
    const root = document.querySelector('[data-cart-offer-root]');
    const form = root?.querySelector('.cart-form');
    if (!root || !form) return;

    const checkoutButton = form.querySelector('button[name="checkout"]');
    const status = root.querySelector('[data-gift-sync-status]');
    const normalButtonLabel = checkoutButton?.textContent || 'Commander';
    const endpoints = {
      cart: root.dataset.cartJsonUrl || '/cart.js',
      add: root.dataset.cartAddUrl || '/cart/add.js',
      update: root.dataset.cartUpdateUrl || '/cart/update.js',
      checkout: root.dataset.checkoutUrl || '/checkout'
    };
    const tiers = [
      {
        threshold: 20000,
        variantId: Number(root.dataset.giftArticulationsVariant),
        name: 'Complexe Articulations — Pêche-Abricot',
        tier: '200'
      },
      {
        threshold: 15000,
        variantId: Number(root.dataset.giftEnduranceVariant),
        name: 'Formule sportive Endurance — Cerise',
        tier: '150'
      },
      {
        threshold: 10000,
        variantId: Number(root.dataset.giftD3Variant),
        name: 'Vitamine D3 — 1 pot',
        tier: '100'
      }
    ].filter((tier) => Number.isFinite(tier.variantId) && tier.variantId > 0);
    const giftConfigurationIsValid = tiers.length === 3 && new Set(tiers.map((tier) => tier.variantId)).size === 3;
    const giftsEnabled = root.dataset.giftsEnabled !== 'false' && giftConfigurationIsValid;
    const giftsDisabledMessage = root.dataset.giftsEnabled === 'false'
      ? 'Le programme de cadeaux est temporairement désactivé pour ce test de paiement.'
      : 'Les trois variantes cadeau ne sont pas configurées correctement. Aucun cadeau ne sera ajouté au paiement.';

    let syncing = false;
    let checkoutPreparing = false;
    let checkoutRecoveryTimer = null;
    const setBusy = (busy, message = '') => {
      syncing = busy;
      root.toggleAttribute('data-gift-syncing', busy);
      if (checkoutButton) {
        checkoutButton.disabled = busy;
        checkoutButton.textContent = busy ? 'Mise à jour du cadeau…' : normalButtonLabel;
      }
      if (status) {
        status.hidden = !message;
        status.textContent = message;
      }
    };
    const requestJson = async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(options.headers || {})
        }
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Impossible de mettre à jour le cadeau.');
      }
      return response.json();
    };
    const tierForVariant = (item) => tiers.find((tier) => Number(item.variant_id) === tier.variantId) || null;
    const hasGiftProperty = (item) => ['100', '150', '200'].includes(String(item.properties?._raptor_gift || ''));
    const isGiftCandidate = (item) => Boolean(tierForVariant(item) || hasGiftProperty(item));
    const isZeroPriceGift = (item) => Number(item.original_price ?? item.price ?? -1) === 0
      && Number(item.final_price ?? item.price ?? -1) === 0;
    const isValidGift = (item) => {
      const tier = tierForVariant(item);
      return Boolean(tier
        && String(item.properties?._raptor_gift || '') === tier.tier
        && String(item.properties?._raptor_gift_name || '') === tier.name
        && isZeroPriceGift(item));
    };
    const paidSubtotal = (cart) => {
      let subtotal = Number(cart.total_price || 0);
      const cartDiscounts = [
        ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
        ...(Array.isArray(cart.discount_applications) ? cart.discount_applications : [])
      ];
      let hasAppliedWheyDiscount = cartDiscounts.some((discount) => {
        const amount = Number(discount.total_allocated_amount || discount.amount || 0);
        return amount > 0 && /whey/i.test(String(discount.title || '').trim());
      });
      let wheyQuantity = 0;
      cart.items.forEach((item) => {
        if (isGiftCandidate(item)) {
          subtotal -= Number(item.final_line_price || 0);
          return;
        }
        const discounts = [
          ...(Array.isArray(item.discounts) ? item.discounts : []),
          ...(Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [])
        ];
        if (discounts.some((discount) => Number(discount.amount || 0) > 0 && /whey/i.test(String(discount.title || discount.discount_application?.title || '').trim()))) {
          hasAppliedWheyDiscount = true;
        }
        if (Number(item.original_price || item.price) === 4499 && String(item.handle || '').startsWith('whey-isolate-native-94')) {
          wheyQuantity += Number(item.quantity || 0);
        }
      });
      return Math.max(0, subtotal);
    };
    const matchingTiers = (cart) => {
      if (!giftsEnabled) return [];
      const subtotal = paidSubtotal(cart);
      return tiers.filter((tier) => subtotal >= tier.threshold);
    };
    const hasExpectedGiftSet = (gifts, unlockedTiers) => gifts.length === unlockedTiers.length
      && unlockedTiers.every((tier) => gifts.some((item) => Number(item.variant_id) === tier.variantId
        && Number(item.quantity) === 1
        && isValidGift(item)));
    const giftSummary = (unlockedTiers) => unlockedTiers
      .slice()
      .sort((a, b) => a.threshold - b.threshold)
      .map((tier) => tier.name)
      .join(' + ');
    const updateItems = async (updates) => requestJson(endpoints.update, {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
    const addGifts = async (unlockedTiers) => requestJson(endpoints.add, {
      method: 'POST',
      body: JSON.stringify({
        items: unlockedTiers.map((tier) => ({
          id: tier.variantId,
          quantity: 1,
          properties: {
            _raptor_gift: tier.tier,
            _raptor_gift_name: tier.name,
            Cadeau: tier.name
          }
        }))
      })
    });

    const syncGift = async ({ reload = false } = {}) => {
      if (syncing) return false;
      setBusy(true, 'Vérification de vos cadeaux automatiques…');
      try {
        const cart = await requestJson(endpoints.cart);
        const unlockedTiers = matchingTiers(cart);
        const gifts = cart.items.filter(isGiftCandidate);
        const isAlreadyCorrect = unlockedTiers.length > 0 && hasExpectedGiftSet(gifts, unlockedTiers);
        const hasNoGiftToAdd = unlockedTiers.length === 0 && gifts.length === 0;
        if (isAlreadyCorrect || hasNoGiftToAdd) {
          setBusy(false, giftsEnabled
            ? (unlockedTiers.length
              ? `Cadeaux gratuits déjà ajoutés : ${giftSummary(unlockedTiers)}.`
              : 'Ajoutez des produits pour atteindre votre premier cadeau dès 100 €.')
            : giftsDisabledMessage);
          return false;
        }

        if (gifts.length) {
          const updates = {};
          gifts.forEach((item) => { updates[item.key] = 0; });
          await updateItems(updates);
        }
        if (unlockedTiers.length) {
          const addedPayload = await addGifts(unlockedTiers);
          const addedItems = Array.isArray(addedPayload?.items) ? addedPayload.items : [addedPayload].filter(Boolean);
          if (!hasExpectedGiftSet(addedItems, unlockedTiers)) {
            const cleanup = {};
            addedItems.forEach((item) => { if (item?.key) cleanup[item.key] = 0; });
            if (Object.keys(cleanup).length) await updateItems(cleanup);
            throw new Error('Les produits techniques configurés pour les cadeaux doivent tous avoir un prix de 0,00 €. Corrigez leurs variantes avant de réessayer.');
          }
        }

        if (reload) {
          window.location.reload();
          return true;
        }
        setBusy(false, unlockedTiers.length
          ? `Cadeaux gratuits ajoutés : ${giftSummary(unlockedTiers)}.`
          : 'Les cadeaux ont été retirés car aucun palier n’est plus atteint.');
        return true;
      } catch (error) {
        setBusy(false, error.message || 'Impossible de mettre à jour le cadeau. Réessayez avant de commander.');
        throw error;
      }
    };

    const quantityInputs = [...form.querySelectorAll('input[name="updates[]"]:not([data-gift-quantity]):not([data-bundle-quantity])')];
    const nativeUpdateInputs = [...form.querySelectorAll('input[name="updates[]"]')];
    const suppressNativeCartUpdates = () => {
      nativeUpdateInputs.forEach((input) => {
        if (!input.hasAttribute('name')) return;
        input.dataset.checkoutFieldName = input.getAttribute('name') || 'updates[]';
        input.removeAttribute('name');
      });
    };
    const restoreNativeCartUpdates = () => {
      nativeUpdateInputs.forEach((input) => {
        if (!input.dataset.checkoutFieldName) return;
        input.setAttribute('name', input.dataset.checkoutFieldName);
        delete input.dataset.checkoutFieldName;
      });
    };
    window.addEventListener('pageshow', (event) => {
      restoreNativeCartUpdates();
      if (!event.persisted) return;
      checkoutPreparing = false;
      delete form.dataset.checkoutPrepared;
      setBusy(false);
    });
    quantityInputs.forEach((input) => {
      input.addEventListener('input', () => {
        if (!status) return;
        status.hidden = false;
        status.textContent = 'Vos cadeaux seront ajustés dès la mise à jour du panier.';
      });
    });

    form.addEventListener('submit', async (event) => {
      if (form.dataset.checkoutPrepared === 'true') {
        delete form.dataset.checkoutPrepared;
        return;
      }
      if (event.submitter && event.submitter.name !== 'checkout') {
        restoreNativeCartUpdates();
        checkoutPreparing = false;
        return;
      }
      event.preventDefault();
      if (checkoutPreparing || syncing) return;
      checkoutPreparing = true;
      setBusy(true, 'Mise à jour du panier et de vos cadeaux…');
      try {
        const updates = {};
        quantityInputs.forEach((input) => {
          const line = input.closest('[data-cart-line]');
          if (line?.dataset.lineKey) updates[line.dataset.lineKey] = Math.max(0, Number(input.value || 0));
        });
        if (Object.keys(updates).length) await updateItems(updates);
        syncing = false;
        await syncGift();
        setBusy(true, 'Validation finale du panier…');
        const preparedCart = await requestJson(endpoints.cart);
        const unlockedTiers = matchingTiers(preparedCart);
        if (unlockedTiers.length) {
          const gifts = preparedCart.items.filter(isGiftCandidate);
          if (!hasExpectedGiftSet(gifts, unlockedTiers)) {
            throw new Error('Tous les cadeaux gratuits correspondant à vos paliers n’ont pas pu être préparés. Actualisez le panier puis réessayez avant de commander.');
          }
        }
        const paidLines = preparedCart.items.filter((item) => !isGiftCandidate(item));
        const configuredBundleSkus = new Set(Object.keys(window.RaptorGosBundles || {}));
        const leakedBundleParent = paidLines.find((item) => configuredBundleSkus.has(String(item.sku || ''))
          && !String(item.properties?._gos_bundle_instance || ''));
        if (leakedBundleParent) {
          throw new Error('Une variante d’affichage de bundle a été ajoutée seule au panier. Retirez-la puis ajoutez l’offre depuis sa fiche produit pour créer toutes les lignes requises.');
        }
        const invalidPaidLine = paidLines.find((item) => {
          const unitPrice = Number(item.original_price ?? item.price ?? -1);
          return unitPrice <= 0 || unitPrice > 4499;
        });
        if (invalidPaidLine) {
          throw new Error('Un article du panier ne respecte pas la grille de prix autorisée. Retirez-le puis réessayez.');
        }
        if (paidLines.some((item) => Number(item.quantity) !== 1)) {
          throw new Error('Chaque article payé doit rester sur une ligne distincte avec une quantité de 1. Réduisez la quantité ou choisissez l’offre groupée prévue sur la fiche produit.');
        }
        if (new Set(paidLines.map((item) => Number(item.variant_id))).size !== paidLines.length) {
          throw new Error('Le même article apparaît plusieurs fois avec le même identifiant Shopify. Conservez une seule unité ou utilisez l’offre groupée dédiée.');
        }
        const hasBundleTrace = (item) => {
          const properties = item.properties || {};
          return /^(?:gos-unit-[bw]-|gos-component-)/.test(String(item.handle || ''))
            || ['_gos_bundle_instance', '_gos_bundle_sku', '_gos_bundle_slot', '_gos_line_handle', '_gos_unit_price']
              .some((key) => String(properties[key] || '') !== '');
        };
        const bundleLines = paidLines.filter(hasBundleTrace);
        const incompleteBundleLine = bundleLines.find((item) => {
          const properties = item.properties || {};
          return !String(properties._gos_bundle_instance || '')
            || !String(properties._gos_bundle_sku || '')
            || !String(properties._gos_bundle_slot || '')
            || !String(properties._gos_line_handle || '')
            || !String(properties._gos_unit_price || '');
        });
        if (incompleteBundleLine) {
          throw new Error('Une ligne technique de bundle a perdu ses informations de contrôle. Videz le panier puis ajoutez de nouveau l’offre.');
        }
        const malformedBundleLine = bundleLines.find((item) => Number(item.quantity) !== 1
          || Number(item.original_price ?? item.price ?? -1) !== Number(item.properties?._gos_unit_price || -1));
        if (malformedBundleLine) {
          throw new Error('Une ligne de bundle a été regroupée ou modifiée. Retirez l’offre du panier puis ajoutez-la à nouveau.');
        }
        if (new Set(bundleLines.map((item) => Number(item.variant_id))).size !== bundleLines.length) {
          throw new Error('Deux unités du bundle utilisent encore la même variante Shopify. Importez le nouveau CSV puis ajoutez de nouveau l’offre.');
        }

        const configuredBundles = window.RaptorGosBundles || {};
        const configuredWhey = window.RaptorGosWhey || {};
        const linesByBundleInstance = new Map();
        bundleLines.forEach((item) => {
          const instance = String(item.properties._gos_bundle_instance);
          if (!linesByBundleInstance.has(instance)) linesByBundleInstance.set(instance, []);
          linesByBundleInstance.get(instance).push(item);
        });
        for (const lines of linesByBundleInstance.values()) {
          const skus = new Set(lines.map((item) => String(item.properties._gos_bundle_sku || '')));
          if (skus.size !== 1) {
            throw new Error('Une offre contient des composants provenant de plusieurs bundles. Retirez-la puis ajoutez-la de nouveau.');
          }
          const sku = [...skus][0];
          const orderedLines = lines.slice().sort((left, right) => (
            Number(left.properties._gos_bundle_slot) - Number(right.properties._gos_bundle_slot)
          ));
          const slotsAreComplete = orderedLines.every((item, index) => (
            Number(item.properties._gos_bundle_slot) === index + 1
          ));
          if (!slotsAreComplete) {
            throw new Error('Une offre est incomplète ou contient un emplacement dupliqué. Retirez-la puis ajoutez-la de nouveau.');
          }

          const configuredBundle = configuredBundles[sku];
          if (configuredBundle) {
            const expectedItems = [];
            configuredBundle.items.forEach((item) => {
              const quantity = Math.max(1, Number(item.quantity || 1));
              for (let unit = 0; unit < quantity; unit += 1) expectedItems.push(item);
            });
            const bundleMismatch = orderedLines.length !== expectedItems.length
              || orderedLines.some((item, index) => {
                const expected = expectedItems[index];
                if (!expected) return true;
                const propertyHandle = String(item.properties._gos_line_handle || '');
                const actualHandle = String(item.handle || '');
                return propertyHandle !== String(expected.handle)
                  || actualHandle !== String(expected.handle)
                  || Number(item.properties._gos_unit_price) !== Number(expected.price);
              });
            if (bundleMismatch) {
              throw new Error('Le contenu d’une offre ne correspond plus à sa configuration officielle. Videz le panier puis ajoutez-la de nouveau.');
            }
            continue;
          }

          const wheyMatch = /^WHEY-(2|3)-POTS$/.exec(sku);
          if (!wheyMatch) {
            throw new Error('Une offre groupée n’est pas reconnue par ce thème. Videz le panier puis ajoutez-la de nouveau.');
          }
          const tier = wheyMatch[1];
          const expectedCount = Number(tier);
          const expectedUnitPrice = tier === '2' ? 3999 : 2999;
          const wheyTier = configuredWhey[tier] || {};
          const wheyMismatch = orderedLines.length !== expectedCount
            || orderedLines.some((item, index) => {
              const allowedHandles = Object.values(wheyTier)
                .map((handles) => Array.isArray(handles) ? handles[index] : null)
                .filter(Boolean)
                .map(String);
              const propertyHandle = String(item.properties._gos_line_handle || '');
              const actualHandle = String(item.handle || '');
              return !allowedHandles.includes(propertyHandle)
                || actualHandle !== propertyHandle
                || Number(item.properties._gos_unit_price) !== expectedUnitPrice;
            });
          if (wheyMismatch) {
            throw new Error('L’offre Whey est incomplète ou ne correspond plus aux saveurs configurées. Retirez-la puis ajoutez-la de nouveau.');
          }
        }

        setBusy(false, unlockedTiers.length
          ? `Cadeaux gratuits prêts : ${giftSummary(unlockedTiers)}.`
          : 'Panier vérifié. Ouverture du paiement…');
        if (typeof form.requestSubmit !== 'function') {
          throw new Error('Votre navigateur ne permet pas de valider ce panier en toute sécurité. Mettez-le à jour puis réessayez.');
        }
        // Les quantités ont déjà été appliquées et validées via Cart Ajax.
        // Ne pas les renvoyer par position dans le POST natif : après une
        // suppression ou un changement de cadeau, l'ordre DOM peut différer
        // de cart.items et altérer une autre ligne après notre garde-fou.
        suppressNativeCartUpdates();
        form.dataset.checkoutPrepared = 'true';
        form.requestSubmit(checkoutButton);
        window.clearTimeout(checkoutRecoveryTimer);
        checkoutRecoveryTimer = window.setTimeout(() => {
          if (document.visibilityState !== 'visible') return;
          restoreNativeCartUpdates();
          checkoutPreparing = false;
          delete form.dataset.checkoutPrepared;
          setBusy(false, 'Le paiement ne s’est pas ouvert. Réessayez ou actualisez le panier.');
        }, 15000);
      } catch (error) {
        window.clearTimeout(checkoutRecoveryTimer);
        delete form.dataset.checkoutPrepared;
        restoreNativeCartUpdates();
        checkoutPreparing = false;
        setBusy(false, error.message || 'Impossible de préparer le paiement. Veuillez réessayer.');
      }
    });

    syncGift({ reload: true }).catch(() => {});
  }

  initCartOffer() {
    const root = document.querySelector('[data-cart-offer-root]');
    if (!root) return;
    const lines = [...root.querySelectorAll('[data-cart-line]')];
    if (!lines.length) return;

    const language = document.documentElement.lang || 'fr-FR';
    const currency = root.dataset.currency || 'EUR';
    const money = new Intl.NumberFormat(language, { style: 'currency', currency });
    const formatMoney = (cents) => money.format(Number(cents) / 100);
    const catalogTotal = root.querySelector('[data-cart-catalog-total]');
    const estimatedTotal = root.querySelector('[data-cart-estimated-total]');
    const totalLabel = root.querySelector('[data-cart-total-label]');
    const discountRow = root.querySelector('[data-whey-discount-row]');
    const discountLabel = root.querySelector('[data-whey-discount-label]');
    const discountAmount = root.querySelector('[data-whey-discount-amount]');
    const offers = [...root.querySelectorAll('[data-whey-cart-offer]')];
    const notice = root.querySelector('[data-whey-cart-notice]');
    const cartStatus = root.querySelector('[data-gift-sync-status]');
    const rewardsRoot = root.querySelector('[data-cart-rewards-progress]');
    const rewardsStatus = rewardsRoot?.querySelector('[data-cart-rewards-status]');
    const removeBundleButtons = [...root.querySelectorAll('[data-remove-bundle]')];
    const rewardsTotal = rewardsRoot?.querySelector('[data-cart-rewards-total]');
    const rewardsBar = rewardsRoot?.querySelector('[data-cart-rewards-bar]');
    const rewardsFill = rewardsRoot?.querySelector('[data-cart-rewards-fill]');
    const rewardsCards = [...(rewardsRoot?.querySelectorAll('[data-cart-rewards-tier]') || [])];
    const giftTiers = [
      { threshold: 10000, level: 1, name: 'Vitamine D3 — 1 pot' },
      { threshold: 15000, level: 2, name: 'Formule Endurance — Cerise' },
      { threshold: 20000, level: 3, name: 'Complexe Articulations — Pêche-Abricot' }
    ];

    const serverCartTotal = Number(root.dataset.cartTotal) || 0;
    const serverOriginalCartTotal = Number(root.dataset.cartOriginalTotal) || serverCartTotal;
    const existingWheyDiscount = Number(root.dataset.existingWheyDiscount) || 0;
    const existingOtherDiscount = Number(root.dataset.existingOtherDiscount) || 0;
    const initialOriginalLinesTotal = lines.reduce((sum, line) => {
      if (line.hasAttribute('data-gift-line')) return sum;
      const quantity = Number(line.dataset.initialQuantity) || 0;
      const unitPrice = Number(line.dataset.originalUnitPrice || line.dataset.unitPrice) || 0;
      return sum + (unitPrice * quantity);
    }, 0);
    const originalCartAdjustment = serverOriginalCartTotal - initialOriginalLinesTotal;

    const updateCartRewards = (paidSubtotal) => {
      if (!rewardsRoot) return;
      const eligibleSubtotal = Math.max(0, Number(paidSubtotal) || 0);
      const current = [...giftTiers].reverse().find((tier) => eligibleSubtotal >= tier.threshold) || null;
      const next = giftTiers.find((tier) => eligibleSubtotal < tier.threshold) || null;
      const unlockedRewards = giftTiers.filter((tier) => eligibleSubtotal >= tier.threshold);
      const unlockedNames = unlockedRewards.map((tier) => tier.name).join(' + ');
      const progress = Math.min(100, (eligibleSubtotal / 20000) * 100);

      rewardsRoot.dataset.level = String(current?.level || 0);
      rewardsFill?.style.setProperty('width', `${progress}%`);
      rewardsBar?.setAttribute('aria-valuenow', String(Math.min(200, Math.round(eligibleSubtotal / 100))));
      rewardsBar?.setAttribute('aria-valuetext', `${formatMoney(eligibleSubtotal)} sur ${formatMoney(20000)}`);
      if (rewardsTotal) rewardsTotal.textContent = `Panier éligible : ${formatMoney(eligibleSubtotal)}`;

      if (rewardsStatus) {
        if (!next && current) {
          rewardsStatus.textContent = `Tous les cadeaux sont débloqués et cumulés : ${unlockedNames}.`;
        } else if (current && next) {
          rewardsStatus.textContent = `Cadeaux cumulés débloqués : ${unlockedNames}. Plus que ${formatMoney(next.threshold - eligibleSubtotal)} pour ajouter ${next.name}.`;
        } else if (next) {
          rewardsStatus.textContent = `Plus que ${formatMoney(next.threshold - eligibleSubtotal)} pour débloquer le cadeau gratuit : ${next.name}.`;
        }
      }

      rewardsCards.forEach((card) => {
        const threshold = Number(card.dataset.threshold || 0);
        const unlocked = eligibleSubtotal >= threshold;
        const active = threshold === current?.threshold;
        const state = card.querySelector('[data-cart-rewards-tier-state]');
        card.classList.toggle('is-unlocked', unlocked);
        card.classList.toggle('is-current', active);
        if (state) {
          state.textContent = unlocked
            ? 'Cadeau inclus'
            : `Plus que ${formatMoney(Math.max(0, threshold - eligibleSubtotal))}`;
        }
      });
    };

    const update = () => {
      const quantitiesChanged = lines.some((line) => {
        if (line.hasAttribute('data-gift-line') || line.hasAttribute('data-bundle-line')) return false;
        const input = line.querySelector('input[name="updates[]"]:not([data-gift-quantity]):not([data-bundle-quantity])');
        return Math.max(0, Number(input?.value || 0)) !== (Number(line.dataset.initialQuantity) || 0);
      });
      let originalLineSubtotal = 0;
      let itemCount = 0;
      let wheyQuantity = 0;

      lines.forEach((line) => {
        const giftLine = line.hasAttribute('data-gift-line');
        const fixedLine = giftLine || line.hasAttribute('data-bundle-line');
        const input = line.querySelector('input[name="updates[]"]:not([data-gift-quantity]):not([data-bundle-quantity])');
        const quantity = fixedLine
          ? (Number(line.dataset.initialQuantity) || 0)
          : Math.max(0, Number(input?.value || 0));
        const originalUnitPrice = Number(line.dataset.originalUnitPrice || line.dataset.unitPrice) || 0;
        const originalLineTotal = originalUnitPrice * quantity;
        const displayedLineTotal = quantitiesChanged
          ? originalLineTotal
          : Number(line.dataset.finalLinePrice || originalLineTotal);
        if (!giftLine) originalLineSubtotal += originalLineTotal;
        itemCount += quantity;
        if (line.hasAttribute('data-whey-line')) wheyQuantity += quantity;
        const linePrice = line.querySelector('[data-cart-line-total]');
        if (linePrice && !giftLine) linePrice.textContent = formatMoney(displayedLineTotal);
      });

      const currentCatalogTotal = Math.max(0, originalLineSubtotal + originalCartAdjustment);
      const simulatedWheyDiscount = 0;
      const serverDiscountIsCurrent = existingWheyDiscount > 0 && !quantitiesChanged;
      const displayedWheyDiscount = serverDiscountIsCurrent ? existingWheyDiscount : simulatedWheyDiscount;
      const currentEstimatedTotal = serverDiscountIsCurrent
        ? serverCartTotal
        : Math.max(0, currentCatalogTotal - existingOtherDiscount - simulatedWheyDiscount);
      if (catalogTotal) catalogTotal.textContent = formatMoney(currentCatalogTotal);
      if (estimatedTotal) estimatedTotal.textContent = formatMoney(currentEstimatedTotal);
      if (totalLabel) totalLabel.textContent = serverDiscountIsCurrent ? 'Total' : 'Total estimé au paiement';
      if (discountRow) discountRow.hidden = displayedWheyDiscount === 0;
      if (discountLabel) discountLabel.textContent = serverDiscountIsCurrent ? 'Remise whey appliquée' : 'Remise whey estimée';
      const discountRate = '';
      if (discountAmount) discountAmount.textContent = `−${formatMoney(displayedWheyDiscount)}${discountRate ? ` · ${discountRate}` : ''}`;

      offers.forEach((offer) => {
        const quantity = wheyQuantity;
        const title = offer.querySelector('[data-whey-cart-title]');
        const copy = offer.querySelector('[data-whey-cart-copy]');
        offer.hidden = quantity === 0;
        if (title) title.textContent = `${offer.dataset.wheyProductTitle} — ${quantity} pot${quantity > 1 ? 's' : ''}`;
        if (quantity === 1 && copy) {
          copy.textContent = '1 pot sélectionné. Pour passer à 2 ou 3 pots, retirez cette ligne puis choisissez l’offre multi-pots directement sur la fiche Whey.';
        } else if (quantity >= 2 && copy) {
          copy.textContent = 'Pour obtenir les prix multi-pots, choisissez directement l’offre correspondante sur la fiche Whey.';
        }
      });
      const hasWheyItems = wheyQuantity > 0;
      if (notice) {
        notice.hidden = !hasWheyItems;
        notice.textContent = 'Les offres 2 ou 3 pots se choisissent sur la fiche Whey afin d’ajouter chaque pot au bon prix Basic.';
      }

      updateCartRewards(currentEstimatedTotal);

      const cartCount = document.querySelector('.cart-count');
      if (cartCount) cartCount.textContent = String(itemCount);
    };

    lines.forEach((line) => {
      const input = line.querySelector('input[name="updates[]"]:not([data-gift-quantity]):not([data-bundle-quantity])');
      input?.addEventListener('input', update);
      input?.addEventListener('change', update);
    });
    removeBundleButtons.forEach((button) => {
      button.addEventListener('click', async () => {
        const instance = String(button.dataset.removeBundle || '');
        const instanceButtons = removeBundleButtons.filter((candidate) => String(candidate.dataset.removeBundle || '') === instance);
        const updates = {};
        lines.forEach((line) => {
          if (String(line.dataset.bundleInstance || '') === instance && line.dataset.lineKey) {
            updates[line.dataset.lineKey] = 0;
          }
        });
        if (!Object.keys(updates).length) return;
        instanceButtons.forEach((candidate) => {
          candidate.disabled = true;
          candidate.textContent = 'Suppression de l’offre…';
        });
        if (cartStatus) cartStatus.hidden = true;
        try {
          const response = await fetch(root.dataset.cartUpdateUrl || '/cart/update.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ updates })
          });
          if (!response.ok) throw new Error('Suppression impossible');
          window.location.reload();
        } catch (error) {
          instanceButtons.forEach((candidate) => {
            candidate.disabled = false;
            candidate.textContent = 'Retirer l’offre complète';
          });
          if (cartStatus) {
            cartStatus.hidden = false;
            cartStatus.textContent = 'La suppression complète de l’offre a échoué. Actualisez la page puis réessayez avant de commander.';
          }
        }
      });
    });
    window.addEventListener('pageshow', update);
    update();
  }

  initFooterDetails() {
    const details = [...document.querySelectorAll('.footer-details')];
    if (!details.length) return;
    const mobile = window.matchMedia('(max-width: 768px)');
    let previousMode;
    const sync = () => {
      if (mobile.matches && previousMode !== 'mobile') {
        details.forEach((item) => { item.open = false; });
        previousMode = 'mobile';
      } else if (!mobile.matches && previousMode !== 'desktop') {
        details.forEach((item) => { item.open = true; });
        previousMode = 'desktop';
      }
    };
    sync();
    if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change', sync);
    else mobile.addListener(sync);
  }

  initRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach((section) => {
      const url = section.dataset.recommendationsUrl;
      if (!url) return;
      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-recommendations-content]');
          if (content?.innerHTML.trim()) section.querySelector('[data-recommendations-content]').innerHTML = content.innerHTML;
          else section.hidden = true;
        })
        .catch(() => { section.hidden = true; });
    });
  }

  initFacets() {
    const facets = document.querySelector('[data-facets]');
    const open = document.querySelector('[data-filter-toggle]');
    const close = document.querySelector('[data-filter-close]');
    if (!facets || !open) return;
    const hide = () => { facets.classList.remove('is-open'); this.closeOverlay(); };
    open.addEventListener('click', () => { facets.classList.add('is-open'); this.openOverlay(); });
    close?.addEventListener('click', hide);
    this.overlay?.addEventListener('click', hide);
  }

  initAddresses() {
    document.querySelectorAll('[data-address-delete]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!window.confirm(button.dataset.confirmMessage || 'Supprimer cette adresse ?')) return;
        const form = document.createElement('form');
        form.method = 'post';
        form.action = button.dataset.target;
        form.innerHTML = '<input type="hidden" name="_method" value="delete">';
        document.body.appendChild(form);
        form.submit();
      });
    });
  }

  async updateRewardsProgress(scope) {
    const root = scope?.querySelector('[data-rewards-progress]');
    if (!root) return;

    const status = root.querySelector('[data-rewards-progress-status]');
    const total = root.querySelector('[data-rewards-cart-total]');
    const bar = root.querySelector('[data-rewards-progress-bar]');
    const fill = root.querySelector('[data-rewards-progress-fill]');
    const cards = [...scope.querySelectorAll('[data-rewards-tier]')];
    const giftVariantIds = new Set(String(root.dataset.giftVariantIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0));
    const tiers = [
      { threshold: 10000, level: 1, name: 'la Vitamine D3' },
      { threshold: 15000, level: 2, name: 'la formule Endurance' },
      { threshold: 20000, level: 3, name: 'le complexe Articulations' }
    ];

    try {
      const response = await fetch(root.dataset.cartUrl || '/cart.js', {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Panier indisponible');
      const cart = await response.json();
      let paidSubtotal = Number(cart.total_price || 0);
      const cartDiscounts = [
        ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
        ...(Array.isArray(cart.discount_applications) ? cart.discount_applications : [])
      ];
      let hasAppliedWheyDiscount = cartDiscounts.some((discount) => {
        const amount = Number(discount.total_allocated_amount || discount.amount || 0);
        return amount > 0 && /whey/i.test(String(discount.title || '').trim());
      });
      let wheyQuantity = 0;

      (cart.items || []).forEach((item) => {
        if (giftVariantIds.has(Number(item.variant_id)) || item.properties?._raptor_gift) {
          paidSubtotal -= Number(item.final_line_price || 0);
          return;
        }
        const discounts = [
          ...(Array.isArray(item.discounts) ? item.discounts : []),
          ...(Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [])
        ];
        if (discounts.some((discount) => Number(discount.amount || 0) > 0
          && /whey/i.test(String(discount.title || discount.discount_application?.title || '').trim()))) {
          hasAppliedWheyDiscount = true;
        }
        if (Number(item.original_price || item.price) === 4499
          && String(item.handle || '').startsWith('whey-isolate-native-94')) {
          wheyQuantity += Number(item.quantity || 0);
        }
      });

      if (!hasAppliedWheyDiscount) paidSubtotal = Math.max(0, paidSubtotal);

      const currency = cart.currency || 'EUR';
      const money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency
      });
      const formatMoney = (cents) => money.format(Number(cents) / 100);
      const current = [...tiers].reverse().find((tier) => paidSubtotal >= tier.threshold) || null;
      const next = tiers.find((tier) => paidSubtotal < tier.threshold) || null;
      const unlockedRewards = tiers.filter((tier) => paidSubtotal >= tier.threshold);
      const unlockedNames = unlockedRewards.map((tier) => tier.name).join(' + ');
      const progress = Math.min(100, Math.max(0, (paidSubtotal / 20000) * 100));

      root.dataset.level = String(current?.level || 0);
      root.setAttribute('aria-busy', 'false');
      bar?.style.setProperty('--rewards-progress', `${progress}%`);
      if (fill) fill.style.width = `${progress}%`;
      bar?.setAttribute('aria-valuenow', String(Math.min(200, Math.round(paidSubtotal / 100))));
      bar?.setAttribute('aria-valuetext', `${formatMoney(paidSubtotal)} sur ${formatMoney(20000)}`);
      if (total) total.textContent = `Panier éligible : ${formatMoney(paidSubtotal)}`;

      if (status) {
        if (!next && current) {
          status.textContent = `Niveau maximum : vos trois cadeaux sont cumulés (${unlockedNames}).`;
        } else if (current && next) {
          status.textContent = `Cadeaux cumulés débloqués : ${unlockedNames}. Encore ${formatMoney(next.threshold - paidSubtotal)} pour ajouter ${next.name}.`;
        } else if (next) {
          status.textContent = `Encore ${formatMoney(next.threshold - paidSubtotal)} pour débloquer le cadeau gratuit : ${next.name}.`;
        }
      }

      cards.forEach((card) => {
        const threshold = Number(card.dataset.threshold || 0);
        const unlocked = paidSubtotal >= threshold;
        const active = threshold === current?.threshold;
        const state = card.querySelector('[data-rewards-tier-state]');
        card.classList.toggle('is-unlocked', unlocked);
        card.classList.toggle('is-current', active);
        if (state) state.textContent = unlocked ? 'Cadeau inclus' : 'À débloquer';
      });
    } catch (error) {
      root.setAttribute('aria-busy', 'false');
      if (status) status.textContent = 'Ajoutez des produits au panier pour débloquer votre cadeau gratuit.';
      if (total) total.textContent = 'Paliers à 100 €, 150 € et 200 €+';
    }
  }

  initSiteModal() {
    const dialog = document.querySelector('[data-site-modal]');
    const title = dialog?.querySelector('[data-site-modal-title]');
    const content = dialog?.querySelector('[data-site-modal-content]');
    if (!dialog || !title || !content) return;

    let lastTrigger = null;
    let fallbackInertElements = [];

    const templateFor = (key) => [...document.querySelectorAll('[data-site-modal-template]')]
      .find((template) => template.dataset.siteModalTemplate === key);

    const setExpanded = (key, expanded) => {
      document.querySelectorAll('[data-site-modal-open]').forEach((trigger) => {
        if (trigger.dataset.siteModalOpen === key) trigger.setAttribute('aria-expanded', String(expanded));
      });
    };

    const releaseFallbackInert = () => {
      fallbackInertElements.forEach((element) => { element.inert = false; });
      fallbackInertElements = [];
    };

    const cleanup = () => {
      const key = dialog.dataset.siteModalKey;
      if (key) setExpanded(key, false);
      document.body.classList.remove('site-modal-open');
      releaseFallbackInert();
      delete dialog.dataset.siteModalKey;
      content.replaceChildren();
      title.textContent = '';
      if (lastTrigger?.isConnected) lastTrigger.focus();
      lastTrigger = null;
    };

    const closeDialog = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        cleanup();
      }
    };

    const openDialog = (key, trigger) => {
      const template = templateFor(key);
      if (!template) return;

      if (dialog.open) closeDialog();
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      const menuToggle = document.querySelector('[data-menu-toggle]');
      const openedFromMobileMenu = Boolean(mobileMenu?.contains(trigger));
      mobileMenu?.classList.remove('is-open');
      menuToggle?.setAttribute('aria-expanded', 'false');
      this.closeOverlay();
      const searchPanel = document.querySelector('[data-header-search]');
      if (searchPanel) searchPanel.hidden = true;

      lastTrigger = openedFromMobileMenu ? menuToggle : trigger;
      dialog.dataset.siteModalKey = key;
      title.textContent = template.dataset.title || '';
      content.replaceChildren(template.content.cloneNode(true));
      if (key === 'rewards') this.updateRewardsProgress(content);
      setExpanded(key, true);
      document.body.classList.add('site-modal-open');

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        fallbackInertElements = [...document.body.children].filter((element) => element !== dialog);
        fallbackInertElements.forEach((element) => { element.inert = true; });
      }
      window.requestAnimationFrame(() => dialog.querySelector('[data-site-modal-close]')?.focus());
    };

    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-site-modal-open]');
      if (openButton) {
        event.preventDefault();
        openDialog(openButton.dataset.siteModalOpen, openButton);
        return;
      }
      if (event.target.closest('[data-site-modal-close]')) closeDialog();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('close', cleanup);
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const returnedForm = window.location.hash === '#SiteWithdrawalForm'
      ? { key: 'withdrawal', template: templateFor('withdrawal') }
      : window.location.hash === '#SiteContactForm'
        ? { key: 'contact', template: templateFor('contact') }
        : null;
    if (returnedForm?.template?.content.querySelector('.site-dialog__success, .site-dialog__errors')) {
      openDialog(returnedForm.key, null);
    }
  }
}

const initRaptorTheme = () => {
  if (!window.raptorTheme) window.raptorTheme = new RaptorTheme();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initRaptorTheme);
else initRaptorTheme();
